import axios, { AxiosRequestConfig } from 'axios';
import { AutocompleteOption } from 'components/CorlAutocompleteField';
import { useQuery, UseQueryOptions, UseQueryResult } from 'react-query';

export const useLookup = <T extends AutocompleteOption[]>(
  name: string,
  axiosCfg: ExtendedAxiosRequestConfig = {},
  queryOptions: UseQueryOptions<T, Error> = {}
): UseLookupResult<T> => {
  const { queryKey, ...restQueryOptions } = queryOptions;
  const getResult = useQuery<T, Error>({
    queryKey: [name, queryKey].filter(Boolean) as string[],

    queryFn: async (): Promise<T> => {
      const { shouldReturnRawData = false, shouldFilterOutEmptyLabel = true, convertValueToString = false } = axiosCfg;

      const axiosRes = await axios.request<UseGetResponse>({ url: `/app/lookup/e=${name}`, method: 'GET', ...axiosCfg });

      let data = axiosRes.data.Data ?? [];
      if (shouldFilterOutEmptyLabel) data = data.filter((d) => !!d.Label);

      if (shouldReturnRawData) return (data ?? []) as unknown as T;

      if (convertValueToString) return data.map((d) => ({ value: d.Value.toString(), label: d.Label })) as unknown as T;

      return data.map((d) => ({ value: d.Value, label: d.Label })) as unknown as T;
    },

    retry: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...restQueryOptions,
  });

  return getResult;
};

type UseGetResponse = {
  Data: LookUpItem[];
};

type UseLookupResult<T> = UseQueryResult<T, Error>;

type LookUpItem = {
  Value: number;
  Label: string;
  ShortDescription: string;
};

type ExtendedAxiosRequestConfig = AxiosRequestConfig & {
  shouldReturnRawData?: boolean;
  shouldFilterOutEmptyLabel?: boolean;
  convertValueToString?: boolean;
};
